import React, { useState, useRef, useEffect, useCallback } from "react";
import WatermarkedMedia from "./WatermarkedMedia";
import { Play, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

export const Portfolio = ({ projects = [], showHeader = true, mode = "reel" }) => {
  const [active, setActive] = useState(null);
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [projects, updateArrows]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-reel-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Grid mode (used on /portfolio page)
  if (mode === "grid") {
    return (
      <section id="portfolio" className="relative bg-onyx-800 py-28 md:py-36" data-testid="portfolio-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {showHeader && <PortfolioHeader />}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-grid">
            {projects.map((p, idx) => (
              <PortfolioCard key={p.id || idx} project={p} index={idx} onOpen={() => setActive(p)} />
            ))}
          </div>
        </div>
        {active && <ProjectModal project={active} onClose={() => setActive(null)} />}
      </section>
    );
  }

  // Reel mode (default for home)
  return (
    <section id="portfolio" className="relative bg-onyx-800 py-28 md:py-36 overflow-hidden" data-testid="portfolio-section">
      {/* Decorative film perforations */}
      <FilmStripBorder position="top" />

      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        {showHeader && <PortfolioHeader />}

        {/* Reel scroller */}
        <div className="relative mt-4">
          {/* Side fade masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-onyx-800 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-onyx-800 to-transparent" />

          <div
            ref={scrollerRef}
            className="reel-scroller flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory"
            style={{ scrollbarWidth: "none" }}
            data-testid="portfolio-reel"
          >
            {projects.map((p, idx) => (
              <div
                key={p.id || idx}
                data-reel-card
                className="snap-start shrink-0 w-[88vw] sm:w-[420px] md:w-[460px] lg:w-[500px]"
              >
                <PortfolioCard project={p} index={idx} onOpen={() => setActive(p)} />
              </div>
            ))}
          </div>

          {/* Reel controls */}
          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => scrollBy(-1)}
                disabled={!canPrev}
                className="reel-btn group"
                data-testid="reel-prev"
                aria-label="Previous projects"
              >
                <ChevronLeft size={18} />
                <span className="font-cinzel text-[9px] tracking-[0.3em] uppercase ml-2">Previous</span>
              </button>
              <button
                onClick={() => scrollBy(1)}
                disabled={!canNext}
                className="reel-btn group"
                data-testid="reel-next"
                aria-label="More projects"
              >
                <span className="font-cinzel text-[9px] tracking-[0.3em] uppercase mr-2">More</span>
                <ChevronRight size={18} />
              </button>
            </div>
            <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-ivory/40 hidden sm:block">
              {projects.length} Frames • Scroll →
            </p>
          </div>
        </div>
      </div>

      <FilmStripBorder position="bottom" />
      {active && <ProjectModal project={active} onClose={() => setActive(null)} />}

      <style>{`
        .reel-scroller::-webkit-scrollbar { display: none; }
        .reel-btn {
          display: inline-flex; align-items: center;
          padding: 12px 20px;
          border: 1px solid rgba(212,175,55,0.35);
          color: #F5EFE0;
          background: rgba(10,10,10,0.6);
          transition: all 0.4s ease;
        }
        .reel-btn:not(:disabled):hover {
          border-color: #D4AF37;
          background: #D4AF37;
          color: #050505;
        }
        .reel-btn:disabled { opacity: 0.3; cursor: not-allowed; }
      `}</style>
    </section>
  );
};

const PortfolioHeader = () => (
  <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px w-10 bg-gold" />
        <span className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold">
          Featured Works
        </span>
      </div>
      <h2 className="font-display text-4xl md:text-6xl text-ivory leading-[1.05]" data-testid="portfolio-title">
        <span className="italic">Frames of</span>{" "}
        <span className="brand-name not-italic text-gold-gradient text-3xl md:text-5xl">
          Dhadda Motion Pictures
        </span>
      </h2>
      <p className="mt-5 max-w-2xl text-ivory/60">
        A curated reel of music videos, commercials and short films — every one shot, lit
        and finished by Dhadda Motion Pictures. Slide right for more, slide left for previous.
      </p>
    </div>
  </div>
);

const FilmStripBorder = ({ position }) => (
  <div
    className={`pointer-events-none absolute left-0 right-0 ${position === "top" ? "top-0" : "bottom-0"} h-6 flex items-center justify-around opacity-40`}
    aria-hidden="true"
  >
    {Array.from({ length: 40 }).map((_, i) => (
      <div key={`perf-${position}-${i}`} className="w-3 h-3 bg-gold/30 rounded-sm" />
    ))}
  </div>
);

const PortfolioCard = ({ project, index, onOpen }) => (
  <button
    onClick={onOpen}
    className="group relative aspect-video w-full overflow-hidden bg-onyx-700 hover-lift gold-border transition-all"
    data-testid={`portfolio-card-${index}`}
  >
    <WatermarkedMedia
      src={project.custom_thumbnail || project.thumbnail_url}
      alt={project.title}
      position="bottom-right"
      opacity={0.85}
      watermarkSize={48}
      className="h-full w-full transition-transform duration-700 group-hover:scale-105"
      showCaption={false}
    />
    <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-onyx-900/95 via-onyx-900/30 to-transparent p-5">
      <div className="flex items-end justify-between gap-3">
        <div className="text-left">
          <span className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold">
            {project.category || "Project"}
          </span>
          <h3
            className="mt-1 font-display text-xl md:text-2xl text-ivory leading-tight"
            data-testid={`portfolio-title-${index}`}
          >
            {project.title}
          </h3>
        </div>
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-onyx-900/70 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-onyx-900 group-hover:scale-110">
          <Play size={16} fill="currentColor" />
        </div>
      </div>
    </div>
  </button>
);

const ProjectModal = ({ project, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-onyx-900/95 backdrop-blur-md p-4"
    onClick={onClose}
    data-testid="portfolio-modal"
  >
    <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold">
            {project.category}
          </span>
          <h3 className="font-display text-3xl text-ivory mt-1">{project.title}</h3>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={project.youtube_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-ivory/70 hover:text-gold text-xs font-cinzel tracking-[0.2em] uppercase"
            data-testid="modal-youtube-link"
          >
            <ExternalLink size={14} /> YouTube
          </a>
          <button
            onClick={onClose}
            className="text-ivory/70 hover:text-gold border border-gold/30 hover:border-gold w-10 h-10 flex items-center justify-center"
            data-testid="modal-close-btn"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>
      <div className="relative aspect-video w-full bg-black overflow-hidden gold-border">
        <iframe
          src={`https://www.youtube.com/embed/${project.youtube_id}?autoplay=1&rel=0`}
          title={project.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      </div>
    </div>
  </div>
);

export default Portfolio;
